import { FeatureCart } from '@mdlbeasts/nofomo.ui.web';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import { type ReactNode } from 'react';

import { useLocale } from 'hooks/useLocale';
import { useTranslatedText } from 'hooks/useTranslatedText';
import type { Ticket } from 'restful/tickets';
import { TicketStatusFragment } from 'types/api/dato-operations.types';

import BorderStack from 'components/@common/BorderStack';
import Icon from 'components/@icons/Icon';
import { useTicketInfo } from 'components/@tickets/hooks';
import TicketPrice from 'components/@tickets/TicketPrice';

import { isTicketStatusActive, toTicketStatusType } from './utils';

interface Props {
  ticket: Ticket;
  children: ReactNode;
  details?: ReactNode;
  isExpanded?: boolean;
  status?: TicketStatusFragment;
  onOpenDetail?: (ticket: Ticket) => void;
  onToggleExpand?: () => void;
}

const TicketItemRow = ({
  ticket,
  status,
  children,
  details,
  isExpanded = false,
  onOpenDetail,
  onToggleExpand,
}: Props) => {
  const { locale } = useLocale();
  const localize = useTranslatedText();
  const { originalPrice, discountedPrice, isSoldOut } = useTicketInfo(
    ticket.id,
  );
  const isStatusActive = isTicketStatusActive(status);

  const canExpand = !!onToggleExpand;
  const handleRowClick =
    onToggleExpand ?? (onOpenDetail ? () => onOpenDetail(ticket) : undefined);

  return (
    <BorderStack
      sx={{
        gap: 2,
      }}
    >
      <Box
        onClick={handleRowClick}
        sx={{
          cursor: handleRowClick ? 'pointer' : 'default',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <FeatureCart.TicketItem.Promo
          content={localize(ticket.promoMessage)}
          locale={locale}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {canExpand && (
            <Box
              sx={(theme) => ({
                display: 'flex',
                color: 'text.secondary',
                flexShrink: 0,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
              })}
            >
              <Icon name="ChevronDown" />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
              minWidth: 0,
              gap: 2,
            }}
          >
            <Stack
              sx={{
                gap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                variant="small"
                component="h3"
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                }}
              >
                {localize(ticket.name)}
              </Typography>

              <TicketPrice
                originalPrice={originalPrice}
                discountedPrice={discountedPrice}
                color="text.secondary"
              />
            </Stack>

            {!isStatusActive && !isSoldOut && <>{children}</>}
          </Box>
        </Box>

        <FeatureCart.TicketItem.Status
          color={status?.color?.hex}
          content={status?.content}
          type={toTicketStatusType(status?.statusType)}
          schedule={status?.schedule}
          soldOut={isSoldOut}
        />
      </Box>

      {!!details && (
        <Collapse in={isExpanded}>
          <Box
            sx={{
              pt: 2,
            }}
          >
            {details}
          </Box>
        </Collapse>
      )}
    </BorderStack>
  );
};

export default TicketItemRow;
